import { memo, useEffect } from 'react'
import { createPortal } from 'react-dom'

export interface StyleLightboxProps {
  imageUrl: string | null
  onClose: () => void
}

/**
 * Full-screen image lightbox overlay for style preview thumbnails.
 *
 * - Dark backdrop at z-[10000] (above StyleLibraryModal's z-[9999])
 * - Centered full-size image with object-contain
 * - Click outside image or press Escape to dismiss
 * - No navigation arrows (single image only)
 * - Smooth fade-in animation
 * - Portal rendered to document.body
 */
const StyleLightbox = memo(({ imageUrl, onClose }: StyleLightboxProps) => {
  // Escape key handler — fires before modal's handler due to later registration
  useEffect(() => {
    if (!imageUrl) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        e.stopImmediatePropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown, true) // capture phase
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [imageUrl, onClose])

  if (!imageUrl) return null

  const lightbox = (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center animate-fade-in"
      data-testid="style-lightbox"
    >
      {/* Dark backdrop — click here closes the lightbox */}
      <div
        className="absolute inset-0 bg-black/85"
        data-testid="style-lightbox-backdrop"
        onClick={onClose}
      />

      {/* Centered image — clicks here do NOT close */}
      <img
        src={imageUrl}
        alt="Style preview"
        className="relative z-10 max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
        data-testid="style-lightbox-image"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  )

  return createPortal(lightbox, document.body)
})

StyleLightbox.displayName = 'StyleLightbox'
export { StyleLightbox }
